'use client';

import { motion } from 'framer-motion';
import { Navbar } from '@/components/layout/Navbar';
import { Footer } from '@/components/layout/Footer';
import { CheckCircle2, XCircle, ArrowRight, Sparkles, BarChart3, FileUp, Code2, Database } from 'lucide-react';
import Link from 'next/link';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '@/components/ui/accordion';

export default function PlotsAlotVsChatGPTPage() {
  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      <main className="pt-24 pb-16">
        {/* Hero Section */}
        <section className="container mx-auto px-4 md:px-8 mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center max-w-4xl mx-auto">
            <h1 className="text-4xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-primary to-green-600 bg-clip-text text-transparent">
              PlotsAlot vs ChatGPT
            </h1>
            <p className="text-xl text-muted-foreground mb-8">ChatGPT Alternative for Data Analysis & Visualization</p>
            <p className="text-lg text-foreground/80 mb-8 leading-relaxed">
              ChatGPT is a general-purpose AI assistant. PlotsAlot is specialized for data visualization and analysis.
              Here's how they compare.
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <Link
                href="/auth/signin"
                className="inline-flex items-center px-6 py-3 rounded-lg bg-primary text-white hover:bg-primary/90 transition-colors">
                Start Free Trial
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
              <Link
                href="/pricing"
                className="inline-flex items-center px-6 py-3 rounded-lg border border-border hover:bg-accent transition-colors">
                See Pricing
              </Link>
            </div>
          </motion.div>
        </section>

        {/* Key Differences Section */}
        <section className="container mx-auto px-4 md:px-8 mb-16">
          <div className="grid md:grid-cols-3 gap-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-card border border-border rounded-xl p-6">
              <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
                <FileUp className="h-6 w-6 text-primary" />
              </div>
              <h3 className="text-lg font-semibold mb-2">Direct File Upload</h3>
              <p className="text-muted-foreground text-sm">
                Upload CSV, Excel, JSON, Parquet, and PDF files directly. Works with datasets up to millions of rows.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-card border border-border rounded-xl p-6">
              <div className="w-12 h-12 rounded-lg bg-blue-500/10 flex items-center justify-center mb-4">
                <Code2 className="h-6 w-6 text-blue-500" />
              </div>
              <h3 className="text-lg font-semibold mb-2">Python Code Execution</h3>
              <p className="text-muted-foreground text-sm">
                Execute Python with pandas, numpy, matplotlib, seaborn, plotly, scikit-learn pre-installed.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-card border border-border rounded-xl p-6">
              <div className="w-12 h-12 rounded-lg bg-green-500/10 flex items-center justify-center mb-4">
                <Database className="h-6 w-6 text-green-500" />
              </div>
              <h3 className="text-lg font-semibold mb-2">Google Analytics Integration</h3>
              <p className="text-muted-foreground text-sm">
                Connect GA4 directly via OAuth. Analyze website traffic data through natural language.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Pricing Comparison */}
        <section className="container mx-auto px-4 md:px-8 mb-16">
          <h2 className="text-3xl font-bold text-center mb-12">Real Pricing Comparison</h2>

          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {/* ChatGPT Pricing */}
            <div className="bg-card border border-border rounded-xl p-8">
              <h3 className="text-2xl font-bold mb-2">ChatGPT</h3>
              <p className="text-3xl font-bold text-primary mb-4">$0-200/month</p>
              <p className="text-muted-foreground mb-6">
                Free: GPT-3.5. Plus: $20/mo (GPT-4). Team: $25/user/mo. Enterprise: $200+/mo custom.
              </p>

              <div className="space-y-3 mb-6">
                <div className="flex items-start gap-2">
                  <div className="text-green-500 mt-1">✓</div>
                  <div>
                    <p className="font-medium">Free tier</p>
                    <p className="text-sm text-muted-foreground">GPT-3.5 with rate limits</p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <div className="text-green-500 mt-1">✓</div>
                  <div>
                    <p className="font-medium">General AI assistance</p>
                    <p className="text-sm text-muted-foreground">Writing, coding, reasoning</p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <div className="text-green-500 mt-1">✓</div>
                  <div>
                    <p className="font-medium">Advanced Data Analysis (Plus)</p>
                    <p className="text-sm text-muted-foreground">Code Interpreter with limits</p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <div className="text-red-500 mt-1">✗</div>
                  <div>
                    <p className="font-medium">Google Analytics integration</p>
                    <p className="text-sm text-muted-foreground">No direct integrations</p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <div className="text-red-500 mt-1">✗</div>
                  <div>
                    <p className="font-medium">Persistent data context</p>
                    <p className="text-sm text-muted-foreground">Files reset per conversation</p>
                  </div>
                </div>
              </div>
            </div>

            {/* PlotsAlot Pricing */}
            <div className="bg-card border-2 border-primary rounded-xl p-8 relative">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary text-white px-4 py-1 rounded-full text-sm font-medium">
                Specialized for Data
              </div>
              <h3 className="text-2xl font-bold mb-2">PlotsAlot</h3>
              <p className="text-3xl font-bold text-primary mb-4">$0-10/month</p>
              <p className="text-muted-foreground mb-6">Free: 3 messages. Pro: $10/mo (200 messages).</p>

              <div className="space-y-3 mb-6">
                <div className="flex items-start gap-2">
                  <div className="text-green-500 mt-1">✓</div>
                  <div>
                    <p className="font-medium">Free tier</p>
                    <p className="text-sm text-muted-foreground">3 AI messages/month</p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <div className="text-green-500 mt-1">✓</div>
                  <div>
                    <p className="font-medium">Unlimited file uploads</p>
                    <p className="text-sm text-muted-foreground">All subscription tiers</p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <div className="text-green-500 mt-1">✓</div>
                  <div>
                    <p className="font-medium">Python data science stack</p>
                    <p className="text-sm text-muted-foreground">pandas, plotly, scikit-learn</p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <div className="text-green-500 mt-1">✓</div>
                  <div>
                    <p className="font-medium">Google Analytics integration</p>
                    <p className="text-sm text-muted-foreground">OAuth connection included</p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <div className="text-green-500 mt-1">✓</div>
                  <div>
                    <p className="font-medium">Chat history & sharing</p>
                    <p className="text-sm text-muted-foreground">Save and share analyses</p>
                  </div>
                </div>
              </div>

              <Link
                href="/auth/signin"
                className="block w-full text-center py-3 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors">
                Start Free Trial
              </Link>
            </div>
          </div>
        </section>

        {/* Feature Comparison Table */}
        <section className="container mx-auto px-4 md:px-8 mb-16">
          <h2 className="text-3xl font-bold text-center mb-12">Full Feature Breakdown</h2>

          <div className="overflow-x-auto">
            <table className="w-full border-collapse bg-card rounded-xl overflow-hidden">
              <thead className="bg-muted">
                <tr>
                  <th className="text-left p-4 font-semibold">Feature</th>
                  <th className="text-center p-4 font-semibold">PlotsAlot</th>
                  <th className="text-center p-4 font-semibold">ChatGPT</th>
                  <th className="text-left p-4 font-semibold">Winner & Why</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                <tr>
                  <td className="p-4 font-medium">Data File Upload (Free Tier)</td>
                  <td className="p-4 text-center">
                    <CheckCircle2 className="h-5 w-5 text-green-500 mx-auto" />
                  </td>
                  <td className="p-4 text-center">
                    <XCircle className="h-5 w-5 text-red-500 mx-auto" />
                  </td>
                  <td className="p-4 text-sm text-muted-foreground">
                    <span className="font-semibold text-foreground">PlotsAlot</span> - Free tier includes unlimited file
                    uploads. ChatGPT free tier has no file upload.
                  </td>
                </tr>
                <tr>
                  <td className="p-4 font-medium">File Size Limits</td>
                  <td className="p-4 text-center">
                    <span className="text-muted-foreground text-sm">100MB+</span>
                  </td>
                  <td className="p-4 text-center">
                    <span className="text-muted-foreground text-sm">512MB (Plus)</span>
                  </td>
                  <td className="p-4 text-sm text-muted-foreground">
                    <span className="font-semibold text-foreground">ChatGPT</span> - Higher file size limits on Plus
                    tier, but requires $20/mo subscription.
                  </td>
                </tr>
                <tr>
                  <td className="p-4 font-medium">Supported File Formats</td>
                  <td className="p-4 text-center">
                    <CheckCircle2 className="h-5 w-5 text-green-500 mx-auto" />
                  </td>
                  <td className="p-4 text-center">
                    <CheckCircle2 className="h-5 w-5 text-green-500 mx-auto" />
                  </td>
                  <td className="p-4 text-sm text-muted-foreground">
                    <span className="font-semibold text-foreground">Tie</span> - Both support CSV, Excel, JSON.
                    PlotsAlot adds Parquet support.
                  </td>
                </tr>
                <tr>
                  <td className="p-4 font-medium">Python Code Execution</td>
                  <td className="p-4 text-center">
                    <CheckCircle2 className="h-5 w-5 text-green-500 mx-auto" />
                  </td>
                  <td className="p-4 text-center">
                    <span className="text-muted-foreground text-sm">Plus only</span>
                  </td>
                  <td className="p-4 text-sm text-muted-foreground">
                    <span className="font-semibold text-foreground">PlotsAlot</span> - All tiers get Python. ChatGPT
                    requires $20/mo Plus subscription.
                  </td>
                </tr>
                <tr>
                  <td className="p-4 font-medium">Pre-installed Data Science Libraries</td>
                  <td className="p-4 text-center">
                    <CheckCircle2 className="h-5 w-5 text-green-500 mx-auto" />
                  </td>
                  <td className="p-4 text-center">
                    <CheckCircle2 className="h-5 w-5 text-green-500 mx-auto" />
                  </td>
                  <td className="p-4 text-sm text-muted-foreground">
                    <span className="font-semibold text-foreground">Tie</span> - Both include pandas, numpy, matplotlib,
                    seaborn, plotly, scikit-learn.
                  </td>
                </tr>
                <tr>
                  <td className="p-4 font-medium">Custom Package Installation</td>
                  <td className="p-4 text-center">
                    <CheckCircle2 className="h-5 w-5 text-green-500 mx-auto" />
                  </td>
                  <td className="p-4 text-center">
                    <XCircle className="h-5 w-5 text-red-500 mx-auto" />
                  </td>
                  <td className="p-4 text-sm text-muted-foreground">
                    <span className="font-semibold text-foreground">PlotsAlot</span> - Can pip install any package.
                    ChatGPT Code Interpreter has fixed environment.
                  </td>
                </tr>
                <tr>
                  <td className="p-4 font-medium">Persistent Chat History</td>
                  <td className="p-4 text-center">
                    <CheckCircle2 className="h-5 w-5 text-green-500 mx-auto" />
                  </td>
                  <td className="p-4 text-center">
                    <CheckCircle2 className="h-5 w-5 text-green-500 mx-auto" />
                  </td>
                  <td className="p-4 text-sm text-muted-foreground">
                    <span className="font-semibold text-foreground">Tie</span> - Both save conversation history.
                  </td>
                </tr>
                <tr>
                  <td className="p-4 font-medium">File Context Persistence</td>
                  <td className="p-4 text-center">
                    <CheckCircle2 className="h-5 w-5 text-green-500 mx-auto" />
                  </td>
                  <td className="p-4 text-center">
                    <XCircle className="h-5 w-5 text-red-500 mx-auto" />
                  </td>
                  <td className="p-4 text-sm text-muted-foreground">
                    <span className="font-semibold text-foreground">PlotsAlot</span> - Uploaded files persist across
                    chats. ChatGPT files reset per conversation.
                  </td>
                </tr>
                <tr>
                  <td className="p-4 font-medium">Google Analytics Integration</td>
                  <td className="p-4 text-center">
                    <CheckCircle2 className="h-5 w-5 text-green-500 mx-auto" />
                  </td>
                  <td className="p-4 text-center">
                    <XCircle className="h-5 w-5 text-red-500 mx-auto" />
                  </td>
                  <td className="p-4 text-sm text-muted-foreground">
                    <span className="font-semibold text-foreground">PlotsAlot</span> - OAuth connection to GA4. Analyze
                    traffic data directly.
                  </td>
                </tr>
                <tr>
                  <td className="p-4 font-medium">Google Sheets Integration</td>
                  <td className="p-4 text-center">
                    <CheckCircle2 className="h-5 w-5 text-green-500 mx-auto" />
                  </td>
                  <td className="p-4 text-center">
                    <XCircle className="h-5 w-5 text-red-500 mx-auto" />
                  </td>
                  <td className="p-4 text-sm text-muted-foreground">
                    <span className="font-semibold text-foreground">PlotsAlot</span> - Connect Google Sheets directly
                    via OAuth.
                  </td>
                </tr>
                <tr>
                  <td className="p-4 font-medium">Chat Sharing</td>
                  <td className="p-4 text-center">
                    <CheckCircle2 className="h-5 w-5 text-green-500 mx-auto" />
                  </td>
                  <td className="p-4 text-center">
                    <CheckCircle2 className="h-5 w-5 text-green-500 mx-auto" />
                  </td>
                  <td className="p-4 text-sm text-muted-foreground">
                    <span className="font-semibold text-foreground">Tie</span> - Both allow sharing conversations via
                    URL.
                  </td>
                </tr>
                <tr>
                  <td className="p-4 font-medium">Visualization Quality</td>
                  <td className="p-4 text-center">
                    <CheckCircle2 className="h-5 w-5 text-green-500 mx-auto" />
                  </td>
                  <td className="p-4 text-center">
                    <CheckCircle2 className="h-5 w-5 text-green-500 mx-auto" />
                  </td>
                  <td className="p-4 text-sm text-muted-foreground">
                    <span className="font-semibold text-foreground">Tie</span> - Both use matplotlib/plotly for
                    publication-quality charts.
                  </td>
                </tr>
                <tr>
                  <td className="p-4 font-medium">General AI Assistance</td>
                  <td className="p-4 text-center">
                    <span className="text-muted-foreground text-sm">Data-focused</span>
                  </td>
                  <td className="p-4 text-center">
                    <CheckCircle2 className="h-5 w-5 text-green-500 mx-auto" />
                  </td>
                  <td className="p-4 text-sm text-muted-foreground">
                    <span className="font-semibold text-foreground">ChatGPT</span> - Handles writing, coding, reasoning
                    across all domains.
                  </td>
                </tr>
                <tr>
                  <td className="p-4 font-medium">Image Generation</td>
                  <td className="p-4 text-center">
                    <XCircle className="h-5 w-5 text-red-500 mx-auto" />
                  </td>
                  <td className="p-4 text-center">
                    <CheckCircle2 className="h-5 w-5 text-green-500 mx-auto" />
                  </td>
                  <td className="p-4 text-sm text-muted-foreground">
                    <span className="font-semibold text-foreground">ChatGPT</span> - DALL-E integration for AI image
                    generation (Plus tier).
                  </td>
                </tr>
                <tr>
                  <td className="p-4 font-medium">Web Browsing</td>
                  <td className="p-4 text-center">
                    <XCircle className="h-5 w-5 text-red-500 mx-auto" />
                  </td>
                  <td className="p-4 text-center">
                    <CheckCircle2 className="h-5 w-5 text-green-500 mx-auto" />
                  </td>
                  <td className="p-4 text-sm text-muted-foreground">
                    <span className="font-semibold text-foreground">ChatGPT</span> - Can browse web for current
                    information (Plus tier).
                  </td>
                </tr>
                <tr>
                  <td className="p-4 font-medium">API Access</td>
                  <td className="p-4 text-center">
                    <CheckCircle2 className="h-5 w-5 text-green-500 mx-auto" />
                  </td>
                  <td className="p-4 text-center">
                    <span className="text-muted-foreground text-sm">Separate pricing</span>
                  </td>
                  <td className="p-4 text-sm text-muted-foreground">
                    <span className="font-semibold text-foreground">PlotsAlot</span> - REST API included. ChatGPT API is
                    separate pay-per-use.
                  </td>
                </tr>
                <tr>
                  <td className="p-4 font-medium">GPT-4 Access</td>
                  <td className="p-4 text-center">
                    <CheckCircle2 className="h-5 w-5 text-green-500 mx-auto" />
                  </td>
                  <td className="p-4 text-center">
                    <span className="text-muted-foreground text-sm">Plus only</span>
                  </td>
                  <td className="p-4 text-sm text-muted-foreground">
                    <span className="font-semibold text-foreground">PlotsAlot</span> - GPT-4 available on all paid
                    tiers. ChatGPT requires $20/mo Plus.
                  </td>
                </tr>
                <tr>
                  <td className="p-4 font-medium">Claude Access</td>
                  <td className="p-4 text-center">
                    <CheckCircle2 className="h-5 w-5 text-green-500 mx-auto" />
                  </td>
                  <td className="p-4 text-center">
                    <XCircle className="h-5 w-5 text-red-500 mx-auto" />
                  </td>
                  <td className="p-4 text-sm text-muted-foreground">
                    <span className="font-semibold text-foreground">PlotsAlot</span> - Supports both OpenAI and
                    Anthropic models.
                  </td>
                </tr>
                <tr>
                  <td className="p-4 font-medium">Mobile App</td>
                  <td className="p-4 text-center">
                    <span className="text-muted-foreground text-sm">Web only</span>
                  </td>
                  <td className="p-4 text-center">
                    <CheckCircle2 className="h-5 w-5 text-green-500 mx-auto" />
                  </td>
                  <td className="p-4 text-sm text-muted-foreground">
                    <span className="font-semibold text-foreground">ChatGPT</span> - Native iOS and Android apps
                    available.
                  </td>
                </tr>
                <tr>
                  <td className="p-4 font-medium">Voice Input</td>
                  <td className="p-4 text-center">
                    <XCircle className="h-5 w-5 text-red-500 mx-auto" />
                  </td>
                  <td className="p-4 text-center">
                    <CheckCircle2 className="h-5 w-5 text-green-500 mx-auto" />
                  </td>
                  <td className="p-4 text-sm text-muted-foreground">
                    <span className="font-semibold text-foreground">ChatGPT</span> - Voice input and output available
                    (Plus tier).
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        {/* Understanding the Difference */}
        <section className="container mx-auto px-4 md:px-8 mb-16">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold mb-8">Understanding the Difference</h2>

            <div className="prose prose-lg dark:prose-invert max-w-none">
              <p className="text-muted-foreground leading-relaxed mb-6">
                This comparison requires context:{' '}
                <strong className="text-foreground">ChatGPT and PlotsAlot have different specializations</strong>.
              </p>

              <div className="bg-card border border-border rounded-xl p-6 mb-6">
                <h3 className="text-xl font-semibold mb-3 flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg bg-green-500/10 flex items-center justify-center">
                    <Sparkles className="h-4 w-4 text-green-500" />
                  </div>
                  ChatGPT
                </h3>
                <p className="text-muted-foreground mb-0">
                  ChatGPT is a <strong className="text-foreground">general-purpose AI assistant</strong>. It helps with
                  writing, coding, reasoning, research, and conversation across virtually any topic. It's designed to be
                  universally helpful. The Plus tier ($20/mo) adds Advanced Data Analysis (formerly Code Interpreter),
                  DALL-E image generation, GPT-4 access, and web browsing.
                </p>
              </div>

              <div className="bg-card border border-border rounded-xl p-6 mb-6">
                <h3 className="text-xl font-semibold mb-3 flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg gradient-bg flex items-center justify-center">
                    <BarChart3 className="h-4 w-4 text-white" />
                  </div>
                  PlotsAlot
                </h3>
                <p className="text-muted-foreground mb-0">
                  PlotsAlot is a{' '}
                  <strong className="text-foreground">specialized data analysis and visualization platform</strong>.
                  It's purpose-built for uploading datasets, running statistical analysis, creating custom
                  visualizations, and integrating with data sources like Google Analytics. Every feature is optimized
                  for data work, not general AI assistance.
                </p>
              </div>

              <p className="text-lg font-semibold text-foreground mt-8 mb-4">
                The key difference: ChatGPT is a general AI assistant. PlotsAlot is specialized for data.
              </p>
            </div>
          </div>
        </section>

        {/* When to Choose */}
        <section className="container mx-auto px-4 md:px-8 mb-16">
          <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
            {/* Choose PlotsAlot */}
            <div className="bg-card border-2 border-primary rounded-xl p-8">
              <h3 className="text-2xl font-bold mb-6 flex items-center gap-2">
                <CheckCircle2 className="h-6 w-6 text-primary" />
                When to Choose PlotsAlot
              </h3>

              <div className="space-y-6">
                <div>
                  <h4 className="font-semibold text-lg mb-2">1. You Work Primarily with Data</h4>
                  <p className="text-muted-foreground text-sm">
                    If your main use case is analyzing datasets, creating visualizations, or running statistical
                    analysis, PlotsAlot is purpose-built for this workflow. Upload files, ask questions, get charts.
                  </p>
                </div>

                <div>
                  <h4 className="font-semibold text-lg mb-2">2. You Need Google Analytics Integration</h4>
                  <p className="text-muted-foreground text-sm">
                    PlotsAlot connects directly to GA4 via OAuth. Analyze website traffic, conversion rates, and user
                    behavior through natural language. ChatGPT has no integrations.
                  </p>
                </div>

                <div>
                  <h4 className="font-semibold text-lg mb-2">3. You Want Python Without Subscription</h4>
                  <p className="text-muted-foreground text-sm">
                    PlotsAlot includes Python code execution on the free tier. ChatGPT requires $20/mo Plus for Code
                    Interpreter access. Save $240/year if you only need data analysis.
                  </p>
                </div>

                <div>
                  <h4 className="font-semibold text-lg mb-2">4. You Need Custom Package Installation</h4>
                  <p className="text-muted-foreground text-sm">
                    Install any Python package via pip in PlotsAlot. ChatGPT's Code Interpreter has a fixed environment
                    - you can't add packages.
                  </p>
                </div>

                <div>
                  <h4 className="font-semibold text-lg mb-2">5. You Want Persistent File Context</h4>
                  <p className="text-muted-foreground text-sm">
                    Files you upload stay accessible across chats in PlotsAlot. In ChatGPT, files reset when you start a
                    new conversation - you'd need to re-upload every time.
                  </p>
                </div>

                <div>
                  <h4 className="font-semibold text-lg mb-2">6. You Prefer Lower Cost for Data Work</h4>
                  <p className="text-muted-foreground text-sm">
                    PlotsAlot Pro is $19/mo with 1000 messages. ChatGPT Plus is $20/mo with usage limits. For heavy data
                    analysis, PlotsAlot Enterprise ($49/mo unlimited) beats ChatGPT Plus.
                  </p>
                </div>
              </div>
            </div>

            {/* Choose ChatGPT */}
            <div className="bg-card border border-border rounded-xl p-8">
              <h3 className="text-2xl font-bold mb-6 flex items-center gap-2">
                <CheckCircle2 className="h-6 w-6 text-green-500" />
                When to Stick with ChatGPT
              </h3>

              <div className="space-y-6">
                <div>
                  <h4 className="font-semibold text-lg mb-2">1. You Need General AI Assistance</h4>
                  <p className="text-muted-foreground text-sm">
                    ChatGPT excels at writing, editing, brainstorming, tutoring, coding across languages, and answering
                    questions on any topic. It's your all-purpose AI assistant.
                  </p>
                </div>

                <div>
                  <h4 className="font-semibold text-lg mb-2">2. Data Analysis is Occasional</h4>
                  <p className="text-muted-foreground text-sm">
                    If you analyze data occasionally but use AI for many other tasks, ChatGPT Plus ($20/mo) gives you
                    everything including Code Interpreter. No need for multiple tools.
                  </p>
                </div>

                <div>
                  <h4 className="font-semibold text-lg mb-2">3. You Want Image Generation</h4>
                  <p className="text-muted-foreground text-sm">
                    ChatGPT Plus includes DALL-E for creating images from text descriptions. PlotsAlot only creates data
                    visualizations, not creative images.
                  </p>
                </div>

                <div>
                  <h4 className="font-semibold text-lg mb-2">4. You Need Web Browsing</h4>
                  <p className="text-muted-foreground text-sm">
                    ChatGPT Plus can browse the web for current information, news, and research. PlotsAlot works with
                    uploaded files and connected integrations only.
                  </p>
                </div>

                <div>
                  <h4 className="font-semibold text-lg mb-2">5. Mobile Access is Important</h4>
                  <p className="text-muted-foreground text-sm">
                    ChatGPT has native iOS and Android apps with voice input/output. PlotsAlot is web-only
                    (mobile-responsive, but no dedicated app).
                  </p>
                </div>

                <div>
                  <h4 className="font-semibold text-lg mb-2">6. Brand Recognition Matters</h4>
                  <p className="text-muted-foreground text-sm">
                    ChatGPT is the most recognized AI brand globally. If you're presenting work to stakeholders,
                    "created with ChatGPT" carries instant credibility.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Use Case Scenarios */}
        <section className="container mx-auto px-4 md:px-8 mb-16">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold mb-8 text-center">Real-World Use Case Scenarios</h2>

            <div className="space-y-6">
              <div className="bg-card border border-border rounded-xl p-6">
                <h3 className="text-lg font-semibold mb-3">Scenario: Data Scientist / Analyst</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm font-medium text-primary mb-2">Use PlotsAlot if:</p>
                    <ul className="text-sm text-muted-foreground space-y-1 list-disc list-inside">
                      <li>Your job is primarily data analysis</li>
                      <li>You work with large CSV/Excel files daily</li>
                      <li>You need quick exploratory analysis</li>
                      <li>You want GA4 integration for web analytics</li>
                      <li>You prefer specialized data workflows</li>
                    </ul>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-green-500 mb-2">Use ChatGPT if:</p>
                    <ul className="text-sm text-muted-foreground space-y-1 list-disc list-inside">
                      <li>You also write documentation, reports</li>
                      <li>You code in multiple languages beyond Python</li>
                      <li>You need research assistance frequently</li>
                      <li>You want one tool for everything</li>
                      <li>Mobile access is important to you</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="bg-card border border-border rounded-xl p-6">
                <h3 className="text-lg font-semibold mb-3">Scenario: Marketing Professional</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm font-medium text-primary mb-2">Use PlotsAlot if:</p>
                    <ul className="text-sm text-muted-foreground space-y-1 list-disc list-inside">
                      <li>You analyze campaign data regularly</li>
                      <li>You use Google Analytics extensively</li>
                      <li>You need custom ROI dashboards</li>
                      <li>You work with ad platform CSVs</li>
                      <li>Visualization quality matters</li>
                    </ul>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-green-500 mb-2">Use ChatGPT if:</p>
                    <ul className="text-sm text-muted-foreground space-y-1 list-disc list-inside">
                      <li>You write copy, emails, ad content</li>
                      <li>You brainstorm campaign ideas</li>
                      <li>You create social media content</li>
                      <li>You need image generation for ads</li>
                      <li>Data analysis is just one task</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="bg-card border border-border rounded-xl p-6">
                <h3 className="text-lg font-semibold mb-3">Scenario: Business Owner / Product Manager</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm font-medium text-primary mb-2">Use PlotsAlot if:</p>
                    <ul className="text-sm text-muted-foreground space-y-1 list-disc list-inside">
                      <li>You track metrics and KPIs closely</li>
                      <li>You analyze customer/sales data</li>
                      <li>You need weekly/monthly reports</li>
                      <li>You integrate with analytics platforms</li>
                      <li>You want shared data conversations</li>
                    </ul>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-green-500 mb-2">Use ChatGPT if:</p>
                    <ul className="text-sm text-muted-foreground space-y-1 list-disc list-inside">
                      <li>You draft emails, memos, documentation</li>
                      <li>You brainstorm product features</li>
                      <li>You research competitors and markets</li>
                      <li>You want general business advice</li>
                      <li>One versatile tool fits your workflow</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="bg-card border border-border rounded-xl p-6">
                <h3 className="text-lg font-semibold mb-3">Scenario: Student / Researcher</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm font-medium text-primary mb-2">Use PlotsAlot if:</p>
                    <ul className="text-sm text-muted-foreground space-y-1 list-disc list-inside">
                      <li>You're doing data-heavy research</li>
                      <li>You need statistical analysis for papers</li>
                      <li>You're learning data science</li>
                      <li>You want to practice Python interactively</li>
                      <li>Budget is limited ($0-19/mo)</li>
                    </ul>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-green-500 mb-2">Use ChatGPT if:</p>
                    <ul className="text-sm text-muted-foreground space-y-1 list-disc list-inside">
                      <li>You need help writing essays</li>
                      <li>You study multiple subjects</li>
                      <li>You want tutoring across topics</li>
                      <li>You're learning programming generally</li>
                      <li>Free tier (GPT-3.5) is sufficient</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ChatGPT Plus vs PlotsAlot Feature Comparison */}
        <section className="container mx-auto px-4 md:px-8 mb-16">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold mb-8">ChatGPT Plus vs PlotsAlot: Which Features Matter?</h2>

            <div className="space-y-4">
              <div className="bg-card border border-border rounded-xl p-6">
                <h3 className="font-semibold mb-3 flex items-center gap-2">
                  <Code2 className="h-5 w-5 text-primary" />
                  Code Interpreter (Advanced Data Analysis)
                </h3>
                <p className="text-sm text-muted-foreground mb-4">
                  This is ChatGPT Plus's main data feature. It lets you upload files and run Python code. Here's what
                  you need to know:
                </p>
                <ul className="text-sm text-muted-foreground space-y-2 list-disc list-inside ml-4">
                  <li>
                    <strong className="text-foreground">File limits:</strong> Code Interpreter accepts files up to
                    512MB, larger than PlotsAlot (100MB in most tiers)
                  </li>
                  <li>
                    <strong className="text-foreground">File persistence:</strong> Files reset when you start a new
                    conversation. PlotsAlot keeps files accessible across chats
                  </li>
                  <li>
                    <strong className="text-foreground">Package installation:</strong> Fixed environment - can't pip
                    install packages. PlotsAlot allows custom packages
                  </li>
                  <li>
                    <strong className="text-foreground">Execution time:</strong> Similar timeouts (~120 seconds) in both
                    platforms
                  </li>
                  <li>
                    <strong className="text-foreground">Libraries:</strong> Both have pandas, numpy, matplotlib,
                    seaborn, plotly, scikit-learn pre-installed
                  </li>
                </ul>
                <p className="text-sm text-primary mt-4 font-medium">
                  ✓ Conclusion: Code Interpreter is powerful, but PlotsAlot offers more flexibility for serious data
                  work.
                </p>
              </div>

              <div className="bg-card border border-border rounded-xl p-6">
                <h3 className="font-semibold mb-3 flex items-center gap-2">
                  <Sparkles className="h-5 w-5 text-green-500" />
                  GPT-4 Access
                </h3>
                <p className="text-sm text-muted-foreground mb-4">
                  ChatGPT Plus includes GPT-4 access for $20/mo. PlotsAlot Pro ($19/mo) and Enterprise ($49/mo) also
                  include GPT-4.
                </p>
                <ul className="text-sm text-muted-foreground space-y-2 list-disc list-inside ml-4">
                  <li>
                    <strong className="text-foreground">ChatGPT:</strong> GPT-4 free tier doesn't exist. Requires $20/mo
                    Plus subscription
                  </li>
                  <li>
                    <strong className="text-foreground">PlotsAlot:</strong> Free tier uses GPT-3.5. Pro tier ($19/mo)
                    unlocks GPT-4 + Claude
                  </li>
                  <li>
                    <strong className="text-foreground">For data work:</strong> Both perform similarly well on analysis
                    tasks. Model choice matters less than tooling
                  </li>
                </ul>
              </div>

              <div className="bg-card border border-border rounded-xl p-6">
                <h3 className="font-semibold mb-3">Do You Need Both?</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Many professionals use both tools for different purposes:
                </p>
                <div className="grid md:grid-cols-2 gap-4 text-sm">
                  <div className="bg-primary/5 rounded-lg p-4">
                    <p className="font-medium mb-2">Use ChatGPT for:</p>
                    <ul className="text-muted-foreground space-y-1 list-disc list-inside">
                      <li>General writing and editing</li>
                      <li>Code help across languages</li>
                      <li>Research and brainstorming</li>
                      <li>Quick mobile queries</li>
                    </ul>
                  </div>
                  <div className="bg-green-500/5 rounded-lg p-4">
                    <p className="font-medium mb-2">Use PlotsAlot for:</p>
                    <ul className="text-muted-foreground space-y-1 list-disc list-inside">
                      <li>Serious data analysis projects</li>
                      <li>GA4 / Google Sheets integration</li>
                      <li>Persistent data workflows</li>
                      <li>Custom visualization dashboards</li>
                    </ul>
                  </div>
                </div>
                <p className="text-sm text-muted-foreground mt-4">
                  <strong className="text-foreground">Combined cost:</strong> ChatGPT Plus ($20) + PlotsAlot Pro ($19) =
                  $39/mo for both general AI and specialized data analysis.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Honest Limitations */}
        <section className="container mx-auto px-4 md:px-8 mb-16">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold mb-8">Honest Limitations</h2>

            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-card border border-border rounded-xl p-6">
                <h3 className="text-xl font-semibold mb-4 text-primary">PlotsAlot Limitations</h3>
                <ul className="space-y-3 text-sm text-muted-foreground">
                  <li className="flex items-start gap-2">
                    <XCircle className="h-4 w-4 text-red-500 mt-0.5 shrink-0" />
                    <span>
                      <strong className="text-foreground">Not general-purpose:</strong> PlotsAlot is optimized for data
                      work. It won't help you write essays or generate creative content.
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <XCircle className="h-4 w-4 text-red-500 mt-0.5 shrink-0" />
                    <span>
                      <strong className="text-foreground">No image generation:</strong> Creates data visualizations
                      only. No DALL-E equivalent for creative images.
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <XCircle className="h-4 w-4 text-red-500 mt-0.5 shrink-0" />
                    <span>
                      <strong className="text-foreground">No web browsing:</strong> Works with uploaded files and
                      integrations. Can't search the web for current information.
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <XCircle className="h-4 w-4 text-red-500 mt-0.5 shrink-0" />
                    <span>
                      <strong className="text-foreground">Web-only:</strong> No native mobile apps. Mobile-responsive
                      web interface only.
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <XCircle className="h-4 w-4 text-red-500 mt-0.5 shrink-0" />
                    <span>
                      <strong className="text-foreground">Smaller file size limits:</strong> 100MB vs ChatGPT's 512MB
                      (though this covers most use cases).
                    </span>
                  </li>
                </ul>
              </div>

              <div className="bg-card border border-border rounded-xl p-6">
                <h3 className="text-xl font-semibold mb-4 text-green-500">ChatGPT Limitations (for data work)</h3>
                <ul className="space-y-3 text-sm text-muted-foreground">
                  <li className="flex items-start gap-2">
                    <XCircle className="h-4 w-4 text-red-500 mt-0.5 shrink-0" />
                    <span>
                      <strong className="text-foreground">Code Interpreter requires Plus:</strong> $20/mo mandatory for
                      any Python data analysis. PlotsAlot free tier includes it.
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <XCircle className="h-4 w-4 text-red-500 mt-0.5 shrink-0" />
                    <span>
                      <strong className="text-foreground">Files don't persist:</strong> Must re-upload files for each
                      new conversation. Frustrating for ongoing analysis.
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <XCircle className="h-4 w-4 text-red-500 mt-0.5 shrink-0" />
                    <span>
                      <strong className="text-foreground">No integrations:</strong> Can't connect Google Analytics,
                      databases, or APIs. Manual CSV export required.
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <XCircle className="h-4 w-4 text-red-500 mt-0.5 shrink-0" />
                    <span>
                      <strong className="text-foreground">Fixed environment:</strong> Can't install custom Python
                      packages. Limited to pre-installed libraries.
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <XCircle className="h-4 w-4 text-red-500 mt-0.5 shrink-0" />
                    <span>
                      <strong className="text-foreground">Not specialized for data:</strong> General-purpose tool. UI
                      and features not optimized for data workflows.
                    </span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="container mx-auto px-4 md:px-8 mb-16">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold mb-8 text-center">Frequently Asked Questions</h2>

            <Accordion type="single" className="space-y-2">
              <AccordionItem value="item-1" className="bg-card border border-border rounded-xl px-6">
                <AccordionTrigger>Is ChatGPT's Code Interpreter better than PlotsAlot?</AccordionTrigger>
                <AccordionContent>
                  Depends on your needs. Code Interpreter is excellent for occasional data analysis within a
                  general-purpose AI tool. PlotsAlot is better for regular data work due to persistent files, custom
                  packages, integrations (Google Analytics), and lower cost if you only need data features.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-2" className="bg-card border border-border rounded-xl px-6">
                <AccordionTrigger>Can I use ChatGPT free tier for data analysis?</AccordionTrigger>
                <AccordionContent>
                  No. ChatGPT free tier (GPT-3.5) doesn't include file uploads or Code Interpreter. You need ChatGPT
                  Plus ($20/mo) for data analysis features. PlotsAlot includes file upload and Python execution on the
                  free tier.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-3" className="bg-card border border-border rounded-xl px-6">
                <AccordionTrigger>Which has better data analysis AI?</AccordionTrigger>
                <AccordionContent>
                  Both use similar AI models (GPT-4, etc.), so analysis quality is comparable. The difference is in
                  tooling: PlotsAlot has data-specific features (GA integration, persistent files, custom packages)
                  while ChatGPT has broader capabilities (web browsing, image generation, general assistance).
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-4" className="bg-card border border-border rounded-xl px-6">
                <AccordionTrigger>Should I get both ChatGPT Plus and PlotsAlot?</AccordionTrigger>
                <AccordionContent>
                  If you do frequent data analysis AND need general AI assistance, having both ($39/mo total) makes
                  sense. Use ChatGPT for writing, research, coding across languages. Use PlotsAlot for serious data
                  projects with GA integration and persistent workflows.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-5" className="bg-card border border-border rounded-xl px-6">
                <AccordionTrigger>Can I migrate my ChatGPT analyses to PlotsAlot?</AccordionTrigger>
                <AccordionContent>
                  You can't directly transfer conversations, but you can easily recreate analyses by uploading the same
                  files to PlotsAlot. Since both use similar Python libraries, code will work nearly identically.
                  PlotsAlot's advantage is that files stay accessible for future work.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-6" className="bg-card border border-border rounded-xl px-6">
                <AccordionTrigger>Does PlotsAlot work with ChatGPT API?</AccordionTrigger>
                <AccordionContent>
                  PlotsAlot uses OpenAI's API internally (you don't need your own API key). If you're a developer using
                  ChatGPT API separately, PlotsAlot won't interfere - they're independent. PlotsAlot also has its own
                  REST API for integrations.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-7" className="bg-card border border-border rounded-xl px-6">
                <AccordionTrigger>Which is better for learning data science?</AccordionTrigger>
                <AccordionContent>
                  PlotsAlot is better for focused data science learning because you can see the generated Python code,
                  install packages, and build a portfolio of saved analyses. ChatGPT Plus works well too, but the lack
                  of persistent files and custom packages is limiting for building projects.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-8" className="bg-card border border-border rounded-xl px-6">
                <AccordionTrigger>Can I share analyses from both platforms?</AccordionTrigger>
                <AccordionContent>
                  Yes, both support sharing. ChatGPT lets you share conversations via link. PlotsAlot has similar chat
                  sharing with public/private settings. PlotsAlot's advantage: shared chats maintain file context, so
                  viewers see the complete analysis.
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
        </section>

        {/* CTA Section */}
        <section className="container mx-auto px-4 md:px-8">
          <div className="max-w-4xl mx-auto text-center bg-gradient-to-r from-primary/10 to-green-500/10 rounded-2xl p-12 border border-primary/20">
            <h2 className="text-3xl font-bold mb-4">Ready for Specialized Data Analysis?</h2>
            <p className="text-lg text-muted-foreground mb-8">
              Try PlotsAlot free. Upload your data, connect Google Analytics, and get insights in minutes.
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <Link
                href="/auth/signin"
                className="inline-flex items-center px-8 py-4 rounded-lg bg-primary text-white hover:bg-primary/90 transition-colors text-lg font-semibold">
                Get Started Free
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
              <Link
                href="/pricing"
                className="inline-flex items-center px-8 py-4 rounded-lg border-2 border-primary hover:bg-primary/5 transition-colors text-lg font-semibold">
                View Pricing
              </Link>
            </div>
            <p className="text-sm text-muted-foreground mt-6">
              Free tier • No credit card required • 3 messages per month
            </p>
          </div>
        </section>

        {/* Other Comparisons */}
        <section className="container mx-auto px-4 md:px-8 mt-16">
          <h3 className="text-2xl font-bold mb-6 text-center">Other Comparisons</h3>
          <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            <Link
              href="/compare/gamma"
              className="bg-card border border-border rounded-xl p-6 hover:border-primary transition-colors">
              <h4 className="font-semibold mb-2">vs Gamma</h4>
              <p className="text-sm text-muted-foreground">AI presentation tool vs data analysis platform</p>
            </Link>
            <Link
              href="/compare/excel"
              className="bg-card border border-border rounded-xl p-6 hover:border-primary transition-colors">
              <h4 className="font-semibold mb-2">vs Excel</h4>
              <p className="text-sm text-muted-foreground">Traditional spreadsheets vs AI-powered analysis</p>
            </Link>
            <Link
              href="/compare/powerbi"
              className="bg-card border border-border rounded-xl p-6 hover:border-primary transition-colors">
              <h4 className="font-semibold mb-2">vs Power BI</h4>
              <p className="text-sm text-muted-foreground">Enterprise BI tools vs conversational analytics</p>
            </Link>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
